import { useEffect, useMemo, useState } from 'react';

import { InvoiceCustomer } from '../../../../../../interfaces/invoiceCustomer';
import { CUSTOMER_STATUS } from '../../../../../../../shared/constants/customer/customerStatus';
import { invoicesService } from '../../../../../../services/invoicesService';

type UseInvoiceCustomerProps = {
  entityId: number;
  invoiceId: number;
};

type UseInvoiceCustomerReturn = {
  invoiceCustomers: InvoiceCustomer[];
  filteredInvoiceCustomers: InvoiceCustomer[];
  activeCustomers: number;
  canceledCustomers: number;
  isSearch: boolean;
  applySearchFilter: (searchTerm: string) => void;
};

export const useInvoiceCustomers = ({
  entityId,
  invoiceId,
}: UseInvoiceCustomerProps): UseInvoiceCustomerReturn => {
  const [invoiceCustomers, setInvoiceCustomers] = useState<InvoiceCustomer[]>([]);
  const [filteredInvoiceCustomers, setFilteredInvoiceCustomers] =
    useState<InvoiceCustomer[]>(invoiceCustomers);

  const activeCustomers = useMemo(
    () =>
      invoiceCustomers.filter((customer) => customer.status === CUSTOMER_STATUS.ACTIVE)
        .length,
    [invoiceCustomers],
  );

  const canceledCustomers = useMemo(
    () =>
      invoiceCustomers.filter((customer) => customer.status === CUSTOMER_STATUS.CANCELED)
        .length,
    [invoiceCustomers],
  );

  const isSearch = filteredInvoiceCustomers.length !== invoiceCustomers.length;

  useEffect(() => {
    getInvoiceCustomers();
  }, []);

  const getInvoiceCustomers = async () => {
    const response = await invoicesService.getInvoiceDetails(entityId, invoiceId);

    const customersWithFullName = response.customers.map((customer) => ({
      ...customer,
      fullName: `${customer.firstName} ${customer.lastName}`,
    }));

    setInvoiceCustomers(customersWithFullName);
    setFilteredInvoiceCustomers(customersWithFullName);
  };

  const applySearchFilter = (searchTerm: string) => {
    if (!searchTerm) {
      setFilteredInvoiceCustomers(invoiceCustomers);
      return;
    }

    const filtered = invoiceCustomers.filter((customer) =>
      customer.fullName.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    setFilteredInvoiceCustomers(filtered);
  };

  return {
    invoiceCustomers,
    filteredInvoiceCustomers,
    activeCustomers,
    canceledCustomers,
    isSearch,
    applySearchFilter,
  };
};
